












































































































































































import Component from 'vue-class-component';
import { Provide } from 'vue-property-decorator';

// Shared types
import { System, Map, Texts } from '@coldtec/cos-types';

// Store types
import {
    SpacesGetter,
    SystemsAction,
    SystemsGetter,
    ToolSystemSuggestionMap,
    ToolSystem,
    SystemSplit,
    FooterFloatingAction,
    CompanyGetter,
} from '@/store/store.types';

// Steps and step types
import Steps from '@/steps/steps';
import { Step } from '@/steps/types';

// The base class for every step
import BaseStep from '../BaseStep.vue';

// Modals for the error, full specifications of a system and filters
import AlertModal from '@/components/modal/Alert.vue';
import SystemsSpecsModal from './internals/SystemsSpecsModal.vue';
import SystemsFiltersModal from './internals/SystemsFiltersModal.vue';
import { InteractiveModalOptions } from '@/components/modal/types';

// Actual suggestions (left) and spaces (right)
import SystemsSuggestions from './internals/SystemsSuggestions.vue';
import SystemsSpaces from './internals/spaces/SystemsSpaces.vue';

/**
 *  Holding the state of the split toggle.
 */
interface SplitToggle {
    /**
     *
     *  If the toggle should be shown
     *
     *  (i) Updated on browser resize
     *
     */
    show: boolean;

    /** Holding the type of splits that should be shown. */
    type: SystemSplit;
}

/**
 *  Holding the texts for every split's interrogation tooltip.
 */
interface SplitTooltips {
    [split: string]: string;
}

/**
 *  Breakpoint (in pixels) below which the
 *  split toggle should be rendered.
 */
const SPLIT_TOGGLE_BREAKPOINT = 840;

/**
 *  The second step: selecting suggested systems per space (or combination of spaces).
 */
@Component({
    components: {
        SystemsSpecsModal,
        SystemsFiltersModal,
        SystemsSuggestions,
        SystemsSpaces,
    },
})
export default class SystemsStep extends BaseStep {

    public $refs!: {
        alertModal: AlertModal,
        specsModal: SystemsSpecsModal;
        filtersModal: SystemsFiltersModal;
        singleSystemSpaces: SystemsSpaces;
        multiSystemSpaces: SystemsSpaces;
    };

    /**
     *  Holding the error state of the systems, which is set
     *  to true when fetching the systems failed.
     */
    private error = false;

    /**
     *
     *  Holding the texts for the tooltips of the splits, used
     *  at both the regular space headings as the split toggle.
     *
     *  (i) Set in the created hook
     *
     */
    private splitTooltip: SplitTooltips = {
        [SystemSplit.Single]: '',
        [SystemSplit.Multi]: '',
    };

    /**
     *  Holding the state of the split toggle.
     */
    private splitToggle: SplitToggle = {
        show: false,
        type: SystemSplit.Single,
    };

    /**
     *  Forwarding the split types to the template.
     */
    private SystemSplit = SystemSplit;

    /**
     *  Holding the watcher remove methods that should be invoked on destroy.
     */
    private watchRemovers: Array<() => void> = [];

    /**
     *  Used to get the filter's active state out of the store.
     */
    private get filtersActive(): boolean {

        return this.$store.getters[SystemsGetter.FILTER_ACTIVE];

    }

    /**
     *  Used to get all the single split suggestions from the store.
     */
    private get singleSuggestions(): ToolSystemSuggestionMap {

        return this.$store.getters[SystemsGetter.SUGGESTIONS_SINGLE];

    }

    /**
     *  Used to get all the multi split suggestions from the store.
     */
    private get multiSuggestions(): ToolSystemSuggestionMap {

        return this.$store.getters[SystemsGetter.SUGGESTIONS_MULTI];

    }

    /**
     *  Used to get the fact if there are single split suggestions,
     *  and the single split spaces should be rendered.
     */
    private get renderSingleSpaces(): boolean {

        let show = Object.keys(this.singleSuggestions).length > 0;

        if (show) {

            show = this.splitToggle.show ? this.splitToggle.type === SystemSplit.Single : true;

        }

        return show;

    }

    /**
     *  Used to get the fact if there are multi split suggestions,
     *  and the multi split spaces should be rendered.
     */
    private get renderMultiSpaces(): boolean {

        let show = Object.keys(this.multiSuggestions).length > 0;

        if (show) {

            show = this.splitToggle.show ? this.splitToggle.type === SystemSplit.Multi : true;

        }

        return show;

    }

    /**
     *  Used to get the fact if the split toggle should be rendered.
     */
    private get renderSplitToggle(): boolean {

        return !!(
            this.splitToggle.show &&
            Object.keys(this.singleSuggestions).length &&
            Object.keys(this.multiSuggestions).length
        );

    }

    /**
     *  Used to get all the texts of the company.
     */
    private get companyTexts(): Texts {

        return this.$store.getters[CompanyGetter.TEXTS];

    }

    /**
     *  Dispatch the action for fetching the suggestions and start
     *  checking the showing of the split toggle when created.
     */
    private created(): void {

        void this.fetchSuggestions();

        this._checkSplitToggle();

        window.addEventListener('resize', this._checkSplitToggle.bind(this));

        /**
         *  Set the tooltip texts from the company texts.
         */
        this.splitTooltip[SystemSplit.Single] = this.companyTexts.help_single_split_tooltip;
        this.splitTooltip[SystemSplit.Multi] = this.companyTexts.help_multi_split_tooltip;

    }

    /**
     *  Set the floating footer action and spaces watcher when mounted.
     */
    private mounted(): void {

        /**
         *
         *  Default settings of the footer's floating action.
         *
         *  (i) Used to set it when first mounted and when
         *  the filter's active state changed.
         *
         */
        const FOOTER_FLOATING_ACTION: FooterFloatingAction = {
            icon: {
                width: 21,
                height: 24,
                path: '<path d="M12.411 24c-.207 0-.408-.07-.573-.2l-3.819-3c-.24-.189-.382-.485-.382-.8v-5.62L.937 6.487C-.035 5.339-.273 3.7.327 2.301S2.257.001 3.722 0H17.28c1.465.002 2.793.903 3.393 2.303s.361 3.038-.611 4.185l-6.696 7.892V23c0 .552-.427 1-.955 1z" />',
            },
            action: this.$refs.filtersModal.open,
            backgroundColor: null,
        };

        /**
         *  Set the opening of the filters as the footer's floating action.
         */
        this.setFooterFloatingAction(FOOTER_FLOATING_ACTION);

        /**
         *  Start watching the active state of the filters
         *  and update the background color of the footer's
         *  floating action respectively.
         */
        const filterActiveWatchRemover = this.$watch(() => this.$store.getters[SystemsGetter.FILTER_ACTIVE], (active: boolean) => {

            this.setFooterFloatingAction({
                ...FOOTER_FLOATING_ACTION,
                backgroundColor: active ? 'var(--ccos-color-orange)' : null,
            });

        });

        /**
         *  Give it some time to let the system spaces mount
         *  and let their `valid()` state be correct.
         */
        setTimeout(() => {

            /**
             *  Start watching the added spaces to check if they
             *  are all valid and update the footer's next button
             *  disabled state accordingly.
             */
            const addedSpacesWatchRemover = this.$watch(
                () => this.$store.getters[SpacesGetter.ADDED],
                this._checkSpaces.bind(this),
                {
                    deep: true,
                    immediate: true,
                },
            );

            /**
             *  Add the watch removers to the Array so they are
             *  invoked before the component is removed.
             */
            this.watchRemovers.push(filterActiveWatchRemover);
            this.watchRemovers.push(addedSpacesWatchRemover);

            /**
             *  Invoke the base step's mounted hook when mounted.
             */
            this.stepMounted();

        }, 750);

    }

    /**
     *  Remove the watchers and resize listener
     *  on destroy to prevent memory leaks.
     */
    private beforeDestroy(): void {

        for (const watchRemover of this.watchRemovers) {

            watchRemover();

        }

        window.removeEventListener('resize', this._checkSplitToggle.bind(this));

    }

    /**
     *
     *  Provide the `SystemHeading` component a method
     *  to open the specs modal for a system.
     *
     *  @param systemGuid The guid of the system to open the specifications for
     *
     */
    @Provide('openSpecs')
    private openSpecs(systemGuid: string): void {

        /**
         *  Find the tool system for which the specs should be opened.
         */
        for (const toolSystem of this.$store.getters[SystemsGetter.SUGGESTIONS_OF_SHOWING_SPACE] as Array<ToolSystem>) {

            if (toolSystem.guid !== systemGuid) {

                continue;

            }

            /**
             *  Find the tool systems' system data to also forward to the specs modal.
             */
            const system: System = (this.$store.getters[SystemsGetter.SUGGESTIONS_SYSTEMS] as Map<System>)[toolSystem.guid];

            this.$refs.specsModal.open(system, toolSystem);

            break;

        }

    }

    /**
     *
     *  Provide the `SystemsSpecsModal` and `SystemsSuggestions`
     *  components a method to toggle the selected state of a
     *  system for the current showing space.
     *
     *  @param systemGuid The guid of the system to toggle the selected state of
     *
     */
    @Provide('toggleSystem')
    private toggleSystem(systemGuid: string): void {

        this.$store.dispatch(SystemsAction.SELECT, systemGuid);

    }

    /**
     *
     *  Used to dispatch an action to fetch the suggestions
     *  and an action to sync the suggestions.
     *
     *  (i) Shows an alert on error
     *
     */
    private async fetchSuggestions(): Promise<void> {

        this.error = false;

        try {

            await this.$store.dispatch(SystemsAction.FETCH_SUGGESTIONS);

            this.$store.dispatch(SystemsAction.SYNC_SUGGESTIONS);

        } catch (_error) {

            this._showFetchingError();

        }

    }

    /**
     *
     *  Used to show the suggestions of a specific space.
     *
     *  @param systemSpaceId The id of the system space to show the system suggestions for
     *
     */
    private showSpaceSuggestion(systemSpaceId: string): void {

        this.$store.dispatch(SystemsAction.SHOW_FOR_SPACE, systemSpaceId);

    }

    /**
     *
     *  Used to show a specific type of split with the toggle.
     *
     *  @param type The type of split to show the spaces for
     *
     */
    private showSplit(type: SystemSplit): void {

        this.splitToggle.type = type;

    }

    /**
     *  Used to check if the split toggle should be
     *  shown, based on the width of the client.
     */
    private _checkSplitToggle(): void {

        const clientWidth: number = document.documentElement.clientWidth;

        this.splitToggle.show = clientWidth < SPLIT_TOGGLE_BREAKPOINT;

    }

    /**
     *  Used to check if every space is valid and to update the
     *  disabled state of the footer's next button accordingly.
     */
    private _checkSpaces(): void {

        /**
         *
         *  The spaces are valid if at least one single split or multi split system has
         *
         *  (i) Users are allowed to only request airconditioning for one room
         */
        let valid = !!(
            this.$refs.singleSystemSpaces && this.$refs.singleSystemSpaces.valid
            ||
            this.$refs.multiSystemSpaces && this.$refs.multiSystemSpaces.valid
        );

        /**
         *  Update the footer's next button accordingly.
         */
        this.setFooterNext({
            step: Steps[Step.Products],
            disabled: !valid,
            action: () => {

                void this.$router.push(Step.Products);

            },
        });

    }

    /**
     *  Used to show an error if fetching the systems failed,
     *  allowing the user to retry by hitting confirm.
     */
    private async _showFetchingError(): Promise<void> {

        this.error = true;

        /**
         *  Construct the options for the error alert.
         */
        const options: InteractiveModalOptions = {
            title: 'Sorry, er is iets fout gegaan',
            body: 'Tijdens het laden van de systemen is er helaas iets fout gegaan.',
            confirm: {
                title: 'Opnieuw proberen',
                content: 'Probeer opnieuw',
            },
        };

        /**
         *  Retry to fetch the system suggestions if the user hits confirm.
         */
        if (await this.$refs.alertModal.open(options)) {

            void this.fetchSuggestions();

        }

    }

}
